import { useState } from "react";
import { Link2, Send, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const STATUS_CLS = {
  pending_payment: "bg-amber-500/15 text-amber-300",
  payment_failed: "bg-red-500/15 text-red-300",
  confirmed: "bg-emerald-500/15 text-emerald-300",
  completed: "bg-neutral-700/60 text-neutral-200",
  cancelled: "bg-red-500/15 text-red-300",
  no_show: "bg-orange-500/15 text-orange-300",
};
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Africa/Nairobi" });

export const DiscoveryAdminRow = ({ r, slots, onChanged }) => {
  const [link, setLink] = useState(r.meeting_link || "");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState("");
  const editable = ["confirmed", "completed", "cancelled", "no_show"].includes(r.status);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  return (
    <tr className="border-t border-neutral-800 align-top" data-testid={`discovery-row-${r.booking_id}`}>
      <td className="px-4 py-3 whitespace-nowrap">
        <p className="font-semibold text-white">{fmtWhen(r.start_at)}</p>
        <p className="text-[11px] text-neutral-500">{r.slot_label}{r.rescheduled_count ? ` · moved ×${r.rescheduled_count}` : ""}</p>
      </td>
      <td className="px-4 py-3">
        <p className="font-semibold text-white">{r.full_name}</p>
        <p className="text-[11px] text-neutral-400">{r.business_name}</p>
        <p className="text-[11px] text-neutral-500">{r.email} · {r.phone}</p>
        <p className="mt-1 max-w-xs text-[11px] text-neutral-400" title={r.brief}>{(r.brief || "").slice(0, 110)}{(r.brief || "").length > 110 ? "…" : ""}</p>
      </td>
      <td className="px-4 py-3 text-xs">{r.format_label}</td>
      <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`discovery-payment-${r.booking_id}`}>
        <span className={`rounded-full px-2 py-0.5 font-bold ${r.payment.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{r.payment.status}</span>
        <p className="mt-1 text-neutral-400">{r.payment.amount ? `${r.payment.currency} ${Number(r.payment.amount).toLocaleString()}` : "—"}</p>
      </td>
      <td className="px-4 py-3 text-xs" data-testid={`discovery-reminder-${r.booking_id}`}>{r.reminder}</td>
      <td className="px-4 py-3">
        {editable ? (
          <select value={r.status} onChange={(e) => run("status", () => api.put(`/admin/discovery-sessions/${r.booking_id}/status`, { status: e.target.value }), "Status updated")}
            className={`rounded-full border-0 bg-neutral-900 px-2.5 py-1 text-xs font-bold ${STATUS_CLS[r.status] || ""}`} data-testid={`discovery-status-${r.booking_id}`}>
            {["confirmed", "completed", "cancelled", "no_show"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        ) : <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_CLS[r.status] || ""}`}>{r.status.replace("_", " ")}</span>}
      </td>
      <td className="px-4 py-3">
        {r.status === "confirmed" && (
          <div className="space-y-2">
            {r.format === "virtual" && (
              <div className="flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5 text-neutral-400" />
                <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://meet.google.com/…"
                  className="w-48 rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs text-white outline-none focus:border-[#0A8B85]" data-testid={`discovery-link-input-${r.booking_id}`} />
                <button type="button" disabled={!!busy || !link} data-testid={`discovery-link-send-${r.booking_id}`}
                  onClick={() => run("link", () => api.post(`/admin/discovery-sessions/${r.booking_id}/meeting-link`, { meeting_link: link }), "Meeting link emailed to client")}
                  className="inline-flex items-center gap-1 rounded-md bg-[#0A8B85] px-2.5 py-1 text-xs font-bold text-white disabled:opacity-40">
                  {busy === "link" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} {r.meeting_link_sent_at ? "Resend" : "Send"}
                </button>
              </div>
            )}
            {r.meeting_link_sent_at && <p className="text-[11px] text-emerald-300">Link sent {fmtWhen(r.meeting_link_sent_at)}</p>}
            <div className="flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 text-neutral-400" />
              <select value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs text-white" data-testid={`discovery-move-select-${r.booking_id}`}>
                <option value="">Move to…</option>
                {slots.filter((s) => s.key !== r.slot).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button type="button" disabled={!!busy || !slot} data-testid={`discovery-move-btn-${r.booking_id}`}
                onClick={() => run("move", () => api.post(`/admin/discovery-sessions/${r.booking_id}/reschedule`, { slot }), "Session moved — client notified")}
                className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-bold text-neutral-200 hover:border-[#0A8B85] disabled:opacity-40">
                {busy === "move" ? "Moving…" : "Move"}
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};
